import { BadgeColor } from '../widgets/badge-single-color/model';

/** Background + text pair for explicit badge colorMap entries. */
export interface GridMapColorPair {
  bg: string;
  text: string;
}

export const BADGE_COLOR_MAP: Record<BadgeColor, string> = {
  primary: 'var(--badge-color-primary)',
  success: 'var(--badge-color-success)',
  danger: 'var(--badge-color-danger)',
  warning: 'var(--badge-color-warning)',
  info: 'var(--badge-color-info)',
  cyan: 'var(--badge-color-cyan)',
};

/** Static gray fallback when no color is configured in badge-style widgets. */
export const BADGE_WIDGET_FALLBACK_PAIR: GridMapColorPair = {
  bg: '#e2e8f0',
  text: '#64748b',
};

export const BADGE_DEFAULT_TEXT_ON_SOLID = '#ffffff';

export function toBadgeColorsFromMapPair(
  pair: GridMapColorPair,
): ResolvedWidgetColors {
  return {
    backgroundColor: pair.bg,
    textColor: pair.text,
  };
}

export function isBadgeColor(value: string): value is BadgeColor {
  return value in BADGE_COLOR_MAP;
}

export function resolveBadgeColorValue(color: BadgeColor | string): string {
  if (isBadgeColor(color)) {
    return BADGE_COLOR_MAP[color];
  }

  return color;
}

export interface ResolveWidgetColorOptions {
  color?: BadgeColor | string;
  customColor?: string;
}

export interface ResolvedWidgetColors {
  backgroundColor: string;
  textColor: string;
}

/**
 * Resolves badge widget colors with priority:
 * customColor > BadgeColor / hex > static gray fallback
 */
export function resolveBadgeWidgetColors(
  options: ResolveWidgetColorOptions = {},
): ResolvedWidgetColors {
  return resolveWidgetColors(options);
}

export function resolveWidgetColors(
  options: ResolveWidgetColorOptions = {},
): ResolvedWidgetColors {
  const { customColor, color } = options;

  if (customColor) {
    return {
      backgroundColor: customColor,
      textColor: BADGE_DEFAULT_TEXT_ON_SOLID,
    };
  }

  if (color) {
    return {
      backgroundColor: resolveBadgeColorValue(color),
      textColor: BADGE_DEFAULT_TEXT_ON_SOLID,
    };
  }

  return toBadgeColorsFromMapPair(BADGE_WIDGET_FALLBACK_PAIR);
}

export function resolveWidgetColor(
  options: ResolveWidgetColorOptions = {},
): string {
  return resolveWidgetColors(options).backgroundColor;
}

export function resolveWidgetTextColor(
  options: { color?: BadgeColor | string; customColor?: string } = {},
): string {
  const { color, customColor } = options;

  if (customColor) {
    return BADGE_DEFAULT_TEXT_ON_SOLID;
  }

  if (color) {
    return resolveBadgeColorValue(color);
  }

  return BADGE_WIDGET_FALLBACK_PAIR.text;
}

export function isGridMapColorPair(value: unknown): value is GridMapColorPair {
  return (
    typeof value === 'object' &&
    value !== null &&
    'bg' in value &&
    'text' in value
  );
}

export function resolveBadgeColorsFromValue(
  colorValue: BadgeColor | string | GridMapColorPair | undefined,
): ResolvedWidgetColors {
  if (colorValue && isGridMapColorPair(colorValue)) {
    return toBadgeColorsFromMapPair(colorValue);
  }

  if (!colorValue) {
    return toBadgeColorsFromMapPair(BADGE_WIDGET_FALLBACK_PAIR);
  }

  if (isBadgeColor(String(colorValue))) {
    return {
      backgroundColor: BADGE_COLOR_MAP[colorValue as BadgeColor],
      textColor: BADGE_DEFAULT_TEXT_ON_SOLID,
    };
  }

  return {
    backgroundColor: colorValue as string,
    textColor: BADGE_DEFAULT_TEXT_ON_SOLID,
  };
}
